import React from "react";
import { Spinner } from "./Spinner";
import { injectStyles } from "./inject-styles";

/* ------------------------------------------------------------------ */
/*  IconButton                                                        */
/* ------------------------------------------------------------------ */

export type IconButtonVariant = "primary" | "secondary" | "ghost" | "destructive";
export type IconButtonSize = "sm" | "md" | "lg";

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  loading?: boolean;
  /** Accessible label (required when the button has no visible text). */
  "aria-label": string;
}

const css = /* css */ `
  .sf-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid transparent;
    border-radius: var(--sf-radius-md);
    cursor: pointer;
    transition: background var(--sf-transition-fast),
                border-color var(--sf-transition-fast),
                color var(--sf-transition-fast),
                box-shadow var(--sf-transition-fast),
                opacity var(--sf-transition-fast);
    user-select: none;
    position: relative;
    padding: 0;
    flex-shrink: 0;
  }

  .sf-icon-btn:focus-visible {
    outline: var(--sf-ring-width) solid var(--sf-ring-color);
    outline-offset: var(--sf-ring-offset);
  }

  .sf-icon-btn:disabled,
  .sf-icon-btn[aria-disabled="true"] {
    opacity: 0.5;
    pointer-events: none;
  }

  /* Sizes (square) */
  .sf-icon-btn--sm { width: 30px; height: 30px; }
  .sf-icon-btn--md { width: 36px; height: 36px; }
  .sf-icon-btn--lg { width: 42px; height: 42px; }

  /* Variants (reuse Button palette) */
  .sf-icon-btn--primary {
    background: var(--sf-primary);
    color: var(--sf-text-inverse);
    border-color: var(--sf-primary);
    box-shadow: var(--sf-shadow-xs);
  }
  .sf-icon-btn--primary:hover { background: var(--sf-primary-hover); border-color: var(--sf-primary-hover); }
  .sf-icon-btn--primary:active { background: var(--sf-primary-active); border-color: var(--sf-primary-active); }

  .sf-icon-btn--secondary {
    background: var(--sf-bg-elevated);
    color: var(--sf-text-secondary);
    border-color: var(--sf-border);
  }
  .sf-icon-btn--secondary:hover { background: var(--sf-gray-50); border-color: var(--sf-border-strong); color: var(--sf-text); }
  .sf-icon-btn--secondary:active { background: var(--sf-gray-100); }

  .sf-icon-btn--ghost {
    background: transparent;
    color: var(--sf-text-muted);
    border-color: transparent;
  }
  .sf-icon-btn--ghost:hover { background: var(--sf-gray-100); color: var(--sf-text); }
  .sf-icon-btn--ghost:active { background: var(--sf-gray-200); }

  .sf-icon-btn--destructive {
    background: var(--sf-error);
    color: var(--sf-text-inverse);
    border-color: var(--sf-error);
  }
  .sf-icon-btn--destructive:hover { background: #b52424; border-color: #b52424; }
  .sf-icon-btn--destructive:active { background: #a12020; border-color: #a12020; }

  /* Loading */
  .sf-icon-btn--loading > :not(.sf-icon-btn__spinner) { visibility: hidden; }
  .sf-icon-btn__spinner {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      variant = "ghost",
      size = "md",
      loading = false,
      children,
      className,
      disabled,
      ...rest
    },
    ref,
  ) => {
    injectStyles("sf-icon-btn", css);

    const classes = [
      "sf-icon-btn",
      `sf-icon-btn--${variant}`,
      `sf-icon-btn--${size}`,
      loading ? "sf-icon-btn--loading" : "",
      className ?? "",
    ]
      .filter(Boolean)
      .join(" ");

    const spinnerSize = size === "sm" ? 14 : size === "lg" ? 20 : 16;

    return (
      <button
        ref={ref}
        className={classes}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...rest}
      >
        {children}
        {loading && (
          <span className="sf-icon-btn__spinner" aria-hidden="true">
            <Spinner size={spinnerSize} color="currentColor" />
          </span>
        )}
      </button>
    );
  },
);

IconButton.displayName = "IconButton";
export { IconButton };
